import api from './api';

/**
 * CashSettlementService - Service pour gérer les règlements amiables (cash) côté frontend
 * 
 * Ces endpoints sont TOTALEMENT INDÉPENDANTS du flux Stripe et ne concernent
 * que les disputes avec paiement en espèces.
 */
class CashSettlementService {
  constructor() {
    // Cache simple : disputeId -> { data, timestamp }
    this.cache = new Map();
    // TTL = 5 minutes
    this.CACHE_TTL = 5 * 60 * 1000;
  }

  /**
   * Récupérer le règlement amiable d'un dispute avec cache
   * @param {number} disputeId - ID du dispute
   * @returns {Promise<Object>}
   */
  async getByDispute(disputeId) {
    try {
      // Vérifier le cache
      const cached = this.cache.get(disputeId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        console.log(`✅ Settlement cache hit for dispute ${disputeId}`);
        return cached.data;
      }

      // Cache miss ou expiré : faire la requête
      const response = await api.get(`/cash-settlements/dispute/${disputeId}`);
      
      // Mettre en cache
      if (response.data?.data) {
        this.cache.set(disputeId, {
          data: response.data,
          timestamp: Date.now()
        });
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Invalider le cache pour un dispute
   * @param {number} disputeId - ID du dispute
   */
  invalidateCache(disputeId) {
    this.cache.delete(disputeId);
  }

  /**
   * Vider tout le cache
   */
  clearCache() {
    this.cache.clear();
  }


  /**
   * Récupérer tous les règlements de l'utilisateur courant
   * @returns {Promise<Object>}
   */
  async getMySettlements() {
    try {
      const response = await api.get('/cash-settlements/my');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Le payeur confirme avoir effectué le paiement
   * @param {number} settlementId - ID du règlement
   * @param {Object} [proofOfPayment] - Preuve de paiement optionnelle
   * @param {number} [disputeId] - ID du dispute (pour invalider le cache)
   * @returns {Promise<Object>}
   */
  async confirmPayerPaid(settlementId, proofOfPayment = null, disputeId = null) {
    try {
      const payload = proofOfPayment ? { proof_of_payment: proofOfPayment } : {};
      const response = await api.post(`/cash-settlements/${settlementId}/confirm-payer`, payload);
      // Invalider le cache après succès
      if (disputeId) this.invalidateCache(disputeId);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Le bénéficiaire confirme avoir reçu le paiement
   * Cette action résout automatiquement le dispute
   * @param {number} settlementId - ID du règlement
   * @param {number} [disputeId] - ID du dispute (pour invalider le cache)
   * @returns {Promise<Object>}
   */
  async confirmRecipientReceived(settlementId, disputeId = null) {
    try {
      const response = await api.post(`/cash-settlements/${settlementId}/confirm-recipient`);
      // Invalider le cache après succès
      if (disputeId) this.invalidateCache(disputeId);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Contester le règlement (bénéficiaire n'a pas reçu le paiement)
   * @param {number} settlementId - ID du règlement
   * @param {string} reason - Raison de la contestation
   * @param {number} [disputeId] - ID du dispute (pour invalider le cache)
   * @returns {Promise<Object>}
   */
  async disputeSettlement(settlementId, reason, disputeId = null) {
    try {
      const response = await api.post(`/cash-settlements/${settlementId}/dispute`, { reason });
      // Invalider le cache après succès
      if (disputeId) this.invalidateCache(disputeId);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Annuler un règlement (admin uniquement)
   * @param {number} settlementId - ID du règlement
   * @param {string} reason - Raison de l'annulation
   * @param {number} [disputeId] - ID du dispute (pour invalider le cache)
   * @returns {Promise<Object>}
   */
  async cancelSettlement(settlementId, reason, disputeId = null) {
    try {
      const response = await api.post(`/cash-settlements/${settlementId}/cancel`, { reason });
      // Invalider le cache après succès
      if (disputeId) this.invalidateCache(disputeId);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ═══════════════════════════════════════════════════════════
  // Admin API methods
  // ═══════════════════════════════════════════════════════════

  /**
   * Liste paginée des règlements espèces (admin)
   */
  async adminList({ page = 1, limit = 20, status, search, dateFrom, dateTo, amountMin, amountMax, sortBy, sortOrder } = {}) {
    try {
      const params = { page, limit };
      if (status) params.status = status;
      if (search) params.search = search;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (amountMin) params.amountMin = amountMin;
      if (amountMax) params.amountMax = amountMax;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      const response = await api.get('/cash-settlements/admin/list', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Statistiques des règlements espèces (admin)
   */
  async adminStats() {
    try {
      const response = await api.get('/cash-settlements/admin/stats');
      return response.data?.data || response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Renvoyer une notification (admin)
   */
  async adminResend(settlementId, notificationType) {
    try {
      const response = await api.post(`/cash-settlements/admin/${settlementId}/resend`, { notificationType });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Forcer l'expiration d'un règlement (admin)
   */
  async adminForceExpire(settlementId) {
    try {
      const response = await api.post(`/cash-settlements/admin/${settlementId}/force-expire`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

const cashSettlementService = new CashSettlementService();
export default cashSettlementService;
